import React, { Dispatch, ReactNode, SetStateAction } from "react";

import CustomDatePicker, {
  DatePickerWrapper,
} from "src/app/components/CustomDatePicker/CustomDatePicker";
import { CustomInputLabel } from "src/app/components/CustomInput/CustomInput";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as PlusGreyIcon } from "src/images/plus-grey.svg";
import { generateNumberSequence } from "src/utils/methods";
import { PaymentType } from "../types";

const percentages = generateNumberSequence({
  startValue: 0.1,
  endValue: 1,
  step: 0.1,
});

interface PaymentConfigProps {
  paymentType: PaymentType;
  selectedPercentage: number | null;
  setSelectedPercentage: Dispatch<SetStateAction<number | null>>;
  setPaymentType: (newPaymentType: PaymentType) => void;
  paymentDate: string;
  setPaymentDate: Dispatch<SetStateAction<string>>;
  isSecondPayment?: boolean;
  uploadFileField: ReactNode;
  paymentFileWsTaskMetaFieldName: string | null;
}

export default function PaymentConfig({
  paymentType,
  selectedPercentage,
  setSelectedPercentage,
  setPaymentType,
  paymentDate,
  setPaymentDate,
  isSecondPayment = false,
  uploadFileField,
  paymentFileWsTaskMetaFieldName,
}: PaymentConfigProps) {
  return (
    <div className="create-payment-modal__payment-config">
      <div className="create-payment-modal__input">
        {paymentType === PaymentType.SplitPayment && (
          <div className="create-payment-modal__payment">
            {isSecondPayment ? (
              <IDHFormattedMessage
                id="ws_second_payment"
                defaultMessage="Second payment"
              />
            ) : (
              <IDHFormattedMessage
                id="ws_first_payment"
                defaultMessage="First payment"
              />
            )}
          </div>
        )}

        <div>
          <CustomInputLabel>
            <IDHFormattedMessage id="ws_amount" defaultMessage="Amount" />
          </CustomInputLabel>
        </div>
        <div className="create-payment-modal__tags">
          {isSecondPayment && selectedPercentage ? (
            <button
              className="button-style-reset create-payment-modal__selectable-tag create-payment-modal__selectable-tag--active"
              onClick={() => setSelectedPercentage(null)}
              aria-label="Select percentage"
              type="button"
            >
              {((1 - selectedPercentage) * 100).toFixed(0)}%
              <PlusGreyIcon
                className="select-tags__selectable-labels__single__add"
                height={14}
                width={14}
              />
            </button>
          ) : (
            percentages.map((percentage) => {
              if (selectedPercentage) {
                if (selectedPercentage === percentage) {
                  return (
                    <button
                      key={percentage}
                      className="button-style-reset create-payment-modal__selectable-tag create-payment-modal__selectable-tag--active"
                      onClick={() => setSelectedPercentage(null)}
                      aria-label="Percentage"
                      type="button"
                    >
                      {percentage * 100}%
                      <PlusGreyIcon
                        className="select-tags__selectable-labels__single__add"
                        height={14}
                        width={14}
                      />
                    </button>
                  );
                }
                return null;
              }
              return (
                <button
                  key={percentage}
                  className="button-style-reset create-payment-modal__selectable-tag"
                  onClick={() => {
                    setSelectedPercentage(percentage);
                    if (percentage === 1) {
                      setPaymentType(PaymentType.SinglePayment);
                    }
                  }}
                  aria-label="Percentage"
                  type="button"
                >
                  {percentage * 100}%
                  <PlusGreyIcon
                    className="select-tags__selectable-labels__single__add"
                    height={14}
                    width={14}
                  />
                </button>
              );
            })
          )}
        </div>
      </div>

      <div className="create-payment-modal__input">
        <div>
          <CustomInputLabel>
            <IDHFormattedMessage
              id="ws_split_single_payments_extension_due_date"
              defaultMessage="Due Date"
            />
          </CustomInputLabel>
        </div>
        <div className="task-meta-fields__value">
          <span className="task-meta-fields__value-content">
            <div className="value-field">
              <DatePickerWrapper value={paymentDate}>
                <CustomDatePicker
                  initialDate={paymentDate}
                  dateChangeHandler={setPaymentDate}
                />
              </DatePickerWrapper>
            </div>
          </span>
        </div>
      </div>

      <div className="create-payment-modal__input">
        <CustomInputLabel>
          {paymentFileWsTaskMetaFieldName ?? (
            <IDHFormattedMessage
              id="ws_split_single_payments_extension_file"
              defaultMessage="File"
            />
          )}
        </CustomInputLabel>

        {uploadFileField}
      </div>
    </div>
  );
}
